import { effect, inject, Injectable, InjectionToken, signal, WritableSignal } from '@angular/core';
import { ZenithToastService } from '../../components/zenith/zenith-toast/zenith-toast.service';
import { HttpClient } from '@angular/common/http';
import { APP_CONFIG } from '../../config/app-config';
import { map, mergeMap, NEVER, of, Subject } from 'rxjs';
import { take } from 'rxjs/operators';
import { Icon, ZenithAppearance } from '../../components/zenith/zenith-enums';
import { TranslateService } from '@ngx-translate/core';
import { newFirebaseMessagingInjectionToken } from './firebase.factory';

export enum FirebaseMessagingStatus {
  ALLOWED = 'ALLOWED',
  NOT_ALLOWED = 'NOT_ALLOWED',
  ERROR = 'ERROR'
}

/**
 * Whether the current browser is driven by an automated tool (e.g. Playwright), which sets
 * `navigator.webdriver`. Used to avoid showing browser-permission toasts (e.g. push notifications)
 * during e2e tests.
 */
export const IS_AUTOMATED_BROWSER = new InjectionToken<boolean>('IS_AUTOMATED_BROWSER', {
  providedIn: 'root',
  factory: () => navigator.webdriver === true
});

@Injectable({
  providedIn: 'root'
})
export class NewFirebaseMessagingService {
  private readonly appConfig = inject(APP_CONFIG);
  private readonly httpClient = inject(HttpClient);
  private readonly zenithToastService = inject(ZenithToastService);
  private readonly translateService = inject(TranslateService);
  private readonly newFirebaseMessaging = inject(newFirebaseMessagingInjectionToken);
  private readonly isAutomatedBrowser = inject(IS_AUTOMATED_BROWSER);

  readonly status$: WritableSignal<null | FirebaseMessagingStatus> = signal(null);

  constructor() {
    effect(() => {
      const status = this.status$();
      switch (status) {
        case FirebaseMessagingStatus.ERROR:
        case FirebaseMessagingStatus.NOT_ALLOWED:
          this.showStatusToast(status);
          break;
      }
    });
  }

  requestPermissionAndSubscribeToken() {
    this.newFirebaseMessaging.requestToken
      .pipe(
        take(1),
        mergeMap((token) => {
          if (!token) {
            this.status$.set(FirebaseMessagingStatus.NOT_ALLOWED);
            return NEVER;
          }
          return this.subscribeAndRegisterFirebaseMessagingToken(token);
        })
      )
      .subscribe({
        next: () => {
          this.status$.set(FirebaseMessagingStatus.ALLOWED);
        },
        error: (error) => {
          console.error('Unable to subscribe to new notifications', error);
          this.status$.set(FirebaseMessagingStatus.ERROR);
        }
      });
  }

  unsubscribeToken() {
    const result = new Subject<boolean>();
    this.newFirebaseMessaging.getToken
      .pipe(
        take(1),
        mergeMap((token: string) =>
          token ? this.deleteFirebaseMessagingToken(token).pipe(map(() => token)) : of(token)
        ),
        mergeMap((token: string) => (token ? this.newFirebaseMessaging.deleteToken(token) : of(false)))
      )
      .subscribe({
        next: (deleted) => result.next(deleted),
        error: (error) => result.error(error),
        complete: () => result.complete() // When the browser (e.g. iOS safari) doesn't correctly support firebase,
        // the Observable is finalized before emitting any value. Given that we
        // don't want to block the logout flow, we just continue.
      });
    return result;
  }

  get messages$() {
    return this.newFirebaseMessaging.messages;
  }

  private showStatusToast(status: FirebaseMessagingStatus.ERROR | FirebaseMessagingStatus.NOT_ALLOWED) {
    if (this.isAutomatedBrowser) {
      return;
    }
    this.zenithToastService.showToast(
      this.translateService.instant(`firebase.messaging.status.${status}.description`),
      ZenithAppearance.ERROR,
      Icon.ERROR
    );
  }

  private subscribeAndRegisterFirebaseMessagingToken(token: string) {
    return this.httpClient.post<void>(`${this.appConfig.newApiUrl}api/firebaseMessaging`, token);
  }

  private deleteFirebaseMessagingToken(token: string) {
    return this.httpClient.delete<void>(`${this.appConfig.newApiUrl}api/firebaseMessaging`, { body: token });
  }
}
